import { INetworkResult } from '@/coreDomainsModule/networkResult';
import {
  GetInventorySuccessResponse,
  InventoryApi,
  UseFilterItem,
  SortType,
  GetInventoryBusinessSuccessResponse,
  ArrivalInfo,
  ObjectFilterInfo,
  ExportInventoryBusinessSuccessResponse,
  BulkModifyTransactionRequestInfo,
  ExportInventoryExcelRequest,
  GetRelatedTransactionsDetailSuccessResponseInfo,
  InventoryAnalyticsOverviewInfo,
  InventoryAnalyticsDetailAggreageBusinessInfo,
  InventoryAnalyticsDetailBaseBusinessInfo,
  InventoryAnalyticsByDimensionAggregateBusinessInfo,
  InventoryAnalyticsByDimensionBaseBusinessInfo,
  InventoryAnalyticsDetailAggreageBusinessWithAlertInfo,
} from '../axios/generated/inventoryManage';
import { IInventoryRepository } from '../interfaces/IInventoryRepository';
import { customAxiosInstance } from '../axios/axiosInstance';
import { IError } from '../interfaces/IError';
import { axiosErrorHandler } from '../axios/axiosError';
import { pollAsyncProcess } from '../composables/useAsyncPolling';
import { MetricType } from '@/inventoryDashboardModule/domains/entities/Dashboard';
import { DashboardAlertSettingInSeverity } from '@/inventoryDashboardModule/domains/entities/DashboardAlert';

/**
 * 在庫管理の永続化を行うリポジトリ
 */
export class InventoryRepository implements IInventoryRepository {
  /**
   * 在庫情報の取得
   * @param sortType - ソートタイプ
   * @param limit - 取得件数
   * @param offset - 取得開始位置
   * @param earlyPeriod - 表示期間の開始日
   * @param latterPeriod - 表示期間の終了日
   * @param filterItemInfo - フィルターの中で、Objectで渡す必要のあるもの
   * @param sortTarget - ソート対象
   * @param filterItem - フィルター
   * @param getInfoOptions - 取得する数量情報を指定するためのオプション。カンマ区切り文字列で渡す
   */
  async get(
    sortType: SortType,
    limit: number,
    offset: number,
    earlyPeriod: string,
    latterPeriod: string,
    filterItemInfo: ObjectFilterInfo = {},
    sortTarget: string,
    filterItem: UseFilterItem = {},
    getInfoOptions: string
  ): Promise<INetworkResult<GetInventorySuccessResponse, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const inventoryApi = new InventoryApi(undefined, undefined, axiosInstance);

      const apiResponse = await inventoryApi.getInventory(
        sortType,
        limit,
        offset,
        earlyPeriod,
        latterPeriod,
        sortTarget,
        filterItem,
        getInfoOptions,
        filterItemInfo
      );
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 0,
                code: '',
                message: '',
              },
            },
          };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 在庫情報の取得
   * @param sortType - ソートタイプ
   * @param limit - 取得件数
   * @param offset - 取得開始位置
   * @param aggregateBusinessId - 集計取引ID
   * @param earlyPeriod - 表示期間の開始日
   * @param latterPeriod - 表示期間の終了日
   * @param filterItemInfo - フィルターの中で、Objectで渡す必要のあるもの
   * @param filterItem - フィルターの一覧
   * @param getInfoOptions - 取得する数量情報を指定するためのオプション。カンマ区切り文字列で渡す
   */
  async getBusiness(
    sortType: SortType,
    limit: number,
    offset: number,
    aggregateBusinessId: string,
    earlyPeriod: string,
    latterPeriod: string,
    filterItemInfo: ObjectFilterInfo = {},
    filterItem?: UseFilterItem,
    getInfoOptions?: string
  ): Promise<INetworkResult<GetInventoryBusinessSuccessResponse, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const inventoryApi = new InventoryApi(undefined, undefined, axiosInstance);

      const apiResponse = await inventoryApi.getInventoryBusiness(
        sortType,
        limit,
        offset,
        aggregateBusinessId,
        earlyPeriod,
        latterPeriod,
        filterItem,
        getInfoOptions,
        filterItemInfo
      );
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data;
          return { status: { type: 'success', data: result } };
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 0,
                code: '',
                message: '',
              },
            },
          };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 置き場移動での入荷情報の更新
   * @param arrivalInfos - 入荷情報
   */
  async bulkModifyArrival(arrivalInfos: ArrivalInfo[]): Promise<INetworkResult<string[], IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const inventoryApi = new InventoryApi(undefined, undefined, axiosInstance);
      const apiResponse = await inventoryApi.bulkModifyArrival({
        bulkModifyArrivalInfo: arrivalInfos,
      });

      switch (apiResponse.status) {
        case 200:
          const data = apiResponse.data.result.arrivalIds ?? [];
          return {
            status: {
              type: 'success',
              data,
            },
          };
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 0,
                code: '',
                message: '',
              },
            },
          };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 数値情報の更新
   * @param request - リクエスト
   */
  async bulkModifyTransaction(request: BulkModifyTransactionRequestInfo): Promise<INetworkResult<void, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const inventoryApi = new InventoryApi(undefined, undefined, axiosInstance);

      const apiResponse = await inventoryApi.bulkModifyTransaction({
        bulkModifyTransactionRequestInfo: request,
      });

      switch (apiResponse.status) {
        case 200:
          return {
            status: {
              type: 'success',
              data: undefined,
            },
          };
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: apiResponse.status,
                code: '',
                message: apiResponse.data.message,
              },
            },
          };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 在庫情報のExport(Excel) - テンプレートベース
   * @param request - エクスポートリクエスト
   */
  async exportExcel(request: ExportInventoryExcelRequest): Promise<INetworkResult<ExportInventoryBusinessSuccessResponse, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const inventoryApi = new InventoryApi(undefined, undefined, axiosInstance);

      const apiResponse = await inventoryApi.exportInventoryBusiness(request);
      if (apiResponse.status !== 200) {
        throw new Error('想定外のレスポンス');
      }

      const bodyStatusCode = apiResponse.data.statusCode;
      if (bodyStatusCode === 202) {
        const asyncResult = apiResponse.data.result as unknown as { requestId?: string };
        if (!asyncResult.requestId) {
          throw new Error('非同期レスポンスにrequestIdが含まれていません');
        }

        const pollingResult = await pollAsyncProcess<ExportInventoryBusinessSuccessResponse['result']>(asyncResult.requestId, {
          actionType: 'export-inventory-business',
          maxWaitMs: 600000,
        });

        if (pollingResult.status === 'failed') {
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 500,
                code: 'INTERNAL_SERVER_ERROR',
                message: pollingResult.errorMessage ?? '在庫エクスポート処理が失敗しました',
              },
            },
          };
        }

        if (!pollingResult.result) {
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 500,
                code: 'INTERNAL_SERVER_ERROR',
                message: '非同期処理の結果が取得できませんでした',
              },
            },
          };
        }

        const asyncResponse: ExportInventoryBusinessSuccessResponse = {
          statusCode: 200,
          code: 200,
          message: 'success',
          result: pollingResult.result,
        };
        return { status: { type: 'success', data: asyncResponse } };
      }

      const result = apiResponse.data;
      return { status: { type: 'success', data: result } };
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 関連取引の販売実績・内示データを取得する
   * @param aggregateBusinessId - 集計取引ID
   * @param startYear - 開始年
   * @param startMonth - 開始月
   * @param endYear - 終了年
   * @param endMonth - 終了月
   * @param receiptMonthOffset - 受領月オフセット（Nヶ月前内示を指定する）
   */
  async getRelatedTransactionsDetail(
    aggregateBusinessId: string,
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number,
    receiptMonthOffset?: number
  ): Promise<INetworkResult<GetRelatedTransactionsDetailSuccessResponseInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const inventoryApi = new InventoryApi(undefined, undefined, axiosInstance);

      const apiResponse = await inventoryApi.getRelatedTransactionsDetail(
        aggregateBusinessId,
        startYear,
        startMonth,
        endYear,
        endMonth,
        receiptMonthOffset
      );

      switch (apiResponse.status) {
        case 200: {
          const result = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        }
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: apiResponse.status,
                code: '',
                message: apiResponse.data?.message || '',
              },
            },
          };
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 在庫情報の分析概要データを取得する
   * @returns - 在庫情報の分析概要データ
   */
  async getInventoryAnalyticsOverview(): Promise<INetworkResult<InventoryAnalyticsOverviewInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const inventoryApi = new InventoryApi(undefined, undefined, axiosInstance);

      const apiResponse = await inventoryApi.getInventoryAnalyticsOverview();

      switch (apiResponse.status) {
        case 200: {
          const result = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        }
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: apiResponse.status,
                code: '',
                message: apiResponse.data?.message || '',
              },
            },
          };
      }
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }

  /**
   * 在庫情報の分析詳細データ(集計)を取得する
   * @param sortType - ソートタイプ
   * @param sortTarget - ソート対象
   * @param limit - 表示制限
   * @param offset - オフセット
   * @param filterItem - フィルター情報
   * @returns - 在庫情報の分析詳細データ(集計)
   */
  async getInventoryAnalyticsDetailsAggregateBusiness(
    sortType?: string,
    sortTarget?: string,
    limit?: number,
    offset?: number,
    filterItem?: UseFilterItem
  ): Promise<INetworkResult<InventoryAnalyticsDetailAggreageBusinessInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const inventoryApi = new InventoryApi(undefined, undefined, axiosInstance);

      const apiResponse = await inventoryApi.getInventoryAnalyticsDetailsAggregatebusiness(sortType, sortTarget, limit, offset, filterItem);

      switch (apiResponse.status) {
        case 200: {
          const result = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        }
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: apiResponse.status,
                code: '',
                message: apiResponse.data?.message || '',
              },
            },
          };
      }
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }

  /**
   * 在庫情報の分析詳細データ(集計)を取得する
   * @param severity - 重大度
   * @param direction - アラート向き
   * @param sortType - ソートタイプ
   * @param sortTarget - ソート対象
   * @param limit - 表示制限
   * @param offset - オフセット
   * @param filterItem - フィルター情報
   * @param alertFilterInfo - アラートのフィルタ情報
   * @param alertSettings - アラートの設定
   * @returns - 在庫情報の分析詳細データ(集計)
   */
  async getInventoryAnalyticsDetailsAggregateBusinessWithAlert(
    severity: 'critical' | 'warning',
    direction: 'excess' | 'shortage',
    sortType?: string,
    sortTarget?: string,
    limit?: number,
    offset?: number,
    filterItem?: UseFilterItem,
    alertFilterInfo?: string,
    alertSettings?: DashboardAlertSettingInSeverity
  ): Promise<INetworkResult<InventoryAnalyticsDetailAggreageBusinessWithAlertInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const inventoryApi = new InventoryApi(undefined, undefined, axiosInstance);

      const apiResponse = await inventoryApi.getInventoryAnalyticsDetailsAggregatebusinessWithAlert(
        severity,
        direction,
        sortType,
        sortTarget,
        limit,
        offset,
        alertFilterInfo,
        filterItem,
        alertSettings?.excess.consecutiveMonths,
        alertSettings?.excess.ratioThreshold,
        alertSettings?.excess.momChangeRateThreshold,
        alertSettings?.excess.minQuantityThreshold,
        alertSettings?.shortage.consecutiveMonths,
        alertSettings?.shortage.ratioThreshold,
        alertSettings?.shortage.momChangeRateThreshold,
        alertSettings?.shortage.minQuantityThreshold
      );

      switch (apiResponse.status) {
        case 200: {
          const result = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        }
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: apiResponse.status,
                code: '',
                message: apiResponse.data?.message || '',
              },
            },
          };
      }
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }

  /**
   * 在庫情報の分析詳細データ(集計)を取得する
   * @param sortType - ソートタイプ
   * @param sortTarget - ソート対象
   * @param limit - 表示制限
   * @param offset - オフセット
   * @param isExcludeNoAccuracy - 内示正答率がない取引を省くかどうか
   * @param filterItem - フィルター情報
   * @param forecastForReceiptName - 受領用内示名フィルタ情報
   * @param forecastForReceiptResponsibleUserId - 受領用内示担当者フィルタ情報
   * @returns - 在庫情報の分析詳細データ(集計)
   */
  async getInventoryAnalyticsDetailsBaseBusiness(
    sortType?: string,
    sortTarget?: string,
    limit?: number,
    offset?: number,
    isExcludeNoAccuracy?: boolean,
    filterItem?: UseFilterItem,
    forecastForReceiptName?: string,
    forecastForReceiptResponsibleUserId?: string
  ): Promise<INetworkResult<InventoryAnalyticsDetailBaseBusinessInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const inventoryApi = new InventoryApi(undefined, undefined, axiosInstance);

      const apiResponse = await inventoryApi.getInventoryAnalyticsDetailsBaseBusiness(
        sortType,
        sortTarget,
        limit,
        offset,
        isExcludeNoAccuracy,
        filterItem,
        forecastForReceiptName,
        forecastForReceiptResponsibleUserId
      );

      switch (apiResponse.status) {
        case 200: {
          const result = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        }
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: apiResponse.status,
                code: '',
                message: apiResponse.data?.message || '',
              },
            },
          };
      }
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }

  /**
   * 在庫分析（集計取引）を切り口別に取得する。
   * @param metricType - 分析種別
   * @param dimensionType - 切り口種別
   * @param targetYear - 対象年
   * @param targetMonth - 対象月
   * @param targetPeriodFromYear - 集計期間開始年
   * @param targetPeriodFromMonth - 集計期間開始月
   * @param targetPeriodToYear - 集計期間終了年
   * @param targetPeriodToMonth - 集計期間終了月
   * @param forecastAccuracyConsiderationMonths - 正答率算出に考慮する過去月数
   * @param sortType - ソート順
   * @param sortTarget - ソート項目
   * @param limit - 取得件数
   * @param offset - 取得開始位置
   * @param filterItem - 絞り込み条件
   * @returns 切り口別分析結果
   */
  async getInventoryAnalyticsByDimensionAggregateBusiness(
    metricType: MetricType,
    dimensionType: string,
    targetYear: number,
    targetMonth: number,
    targetPeriodFromYear: number,
    targetPeriodFromMonth: number,
    targetPeriodToYear: number,
    targetPeriodToMonth: number,
    forecastAccuracyConsiderationMonths?: number,
    sortType?: string,
    sortTarget?: string,
    limit?: number,
    offset?: number,
    filterItem?: UseFilterItem
  ): Promise<INetworkResult<InventoryAnalyticsByDimensionAggregateBusinessInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const inventoryApi = new InventoryApi(undefined, undefined, axiosInstance);

      const apiResponse = await inventoryApi.getInventoryAnalyticsByDimensionAggregateBusiness(
        sortType,
        sortTarget,
        limit,
        offset,
        metricType,
        dimensionType,
        targetYear,
        targetMonth,
        targetPeriodFromYear,
        targetPeriodFromMonth,
        targetPeriodToYear,
        targetPeriodToMonth,
        forecastAccuracyConsiderationMonths,
        filterItem
      );

      switch (apiResponse.status) {
        case 200: {
          const result = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        }
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: apiResponse.status,
                code: '',
                message: apiResponse.data?.message || '',
              },
            },
          };
      }
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }

  /**
   * 在庫分析（関連取引）を切り口別に取得する。
   * @param metricType - 分析種別
   * @param dimensionType - 切り口種別
   * @param targetYear - 対象年
   * @param targetMonth - 対象月
   * @param targetPeriodFromYear - 集計期間開始年
   * @param targetPeriodFromMonth - 集計期間開始月
   * @param targetPeriodToYear - 集計期間終了年
   * @param targetPeriodToMonth - 集計期間終了月
   * @param sortType - ソート順
   * @param sortTarget - ソート項目
   * @param limit - 取得件数
   * @param offset - 取得開始位置
   * @param filterItem - 絞り込み条件
   */
  async getInventoryAnalyticsByDimensionBaseBusiness(
    metricType: MetricType,
    dimensionType: string,
    targetYear: number,
    targetMonth: number,
    targetPeriodFromYear: number,
    targetPeriodFromMonth: number,
    targetPeriodToYear: number,
    targetPeriodToMonth: number,
    sortType?: string,
    sortTarget?: string,
    limit?: number,
    offset?: number,
    filterItem?: UseFilterItem
  ): Promise<INetworkResult<InventoryAnalyticsByDimensionBaseBusinessInfo, IError>> {
    try {
      const axiosInstance = await customAxiosInstance('dbAccess');
      const inventoryApi = new InventoryApi(undefined, undefined, axiosInstance);

      const apiResponse = await inventoryApi.getInventoryAnalyticsByDimensionBaseBusiness(
        sortType,
        sortTarget,
        limit,
        offset,
        metricType,
        dimensionType,
        targetYear,
        targetMonth,
        targetPeriodFromYear,
        targetPeriodFromMonth,
        targetPeriodToYear,
        targetPeriodToMonth,
        filterItem
      );

      switch (apiResponse.status) {
        case 200: {
          const result = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        }
        default:
          new Error('想定外のレスポンス');
          return {
            status: {
              type: 'error',
              error: {
                statusCode: apiResponse.status,
                code: '',
                message: apiResponse.data?.message || '',
              },
            },
          };
      }
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }
}
